import React from 'react';
import { observer } from "mobx-react";
import moment from 'moment'

import Highcharts from 'highcharts/highstock'

import HighchartWithLoadingSpinner from './HighchartWithLoadingSpinner';
import MonitoringV4Links from './MonitoringV4Links'
import { countyRecentPM25Store } from '../store/apiStores'

// fix: add gap over missing hourly datapoints
require('highcharts/modules/broken-axis')(Highcharts)

function processCountyRecentPM25Data(data) {
    const jsonObj = {};
    var meanLine = {};
    let pm25max = 0;
    data.forEach(function(v){
        let datetimeStr = v.local_time.slice(0,-6)
        let datetime = parseInt(moment.utc(datetimeStr).format('x'))
        let pm25 = v.pm25
        if (!jsonObj[v.site_name]){
            jsonObj[v.site_name] = {data: [], name: v.site_name}
        }
        jsonObj[v.site_name]['data'].push([datetime, pm25])
        if (!meanLine[datetime]){
            meanLine[datetime] = {data: [], datetime: datetime}
        }
        meanLine[datetime]['data'].push(pm25)
        if (pm25 > pm25max) {
            pm25max = pm25
        }
    });
    Object.values(jsonObj).forEach((e)=>{e.data.sort()})

    const meanLine_ordered = {};
    Object.keys(meanLine).sort().forEach(function(key) {
        meanLine_ordered[key] = meanLine[key];
    });
    const seriesMean = [];
    Object.values(meanLine_ordered).forEach(function(v){
        let values = v.data.reduce((previous, current) => current += previous);
        values /= v.data.length;
        seriesMean.push([v.datetime, values])
    });
    let seriesData = Object.values(jsonObj);
    if (seriesData.length > 0) {
        seriesData.push({name: 'AVERAGE', color: 'black', data: seriesMean});
    }
    return {series: seriesData, ymax: pm25max}
}

function countyRecentPM253hrData(data) {
    var jsonObj = {};
    let pm25max = 0;
    data.forEach(function(v){
        let datetimeStr = v.local_time.slice(0,-6)
        let datetime = parseInt(moment.utc(datetimeStr).format('x'))
        let pm25 = v.pm25
        if (jsonObj[v.site_name]){
            jsonObj[v.site_name]['data'].push([datetime, pm25])
            jsonObj[v.site_name]['data'].sort()
        } else {
            jsonObj[v.site_name] = []
            jsonObj[v.site_name]['name'] = v.site_name
            jsonObj[v.site_name]['data'] = []
            jsonObj[v.site_name]['data'].push([datetime, pm25])
        }
        if (pm25 > pm25max) {
            pm25max = pm25
        }
    });
    var moveMean = {};
    Object.values(jsonObj).forEach(function(v){
        if (v.data.length >= 3){
            moveMean[v.name] = []
            moveMean[v.name]['name'] = v.name
            moveMean[v.name]['data'] = []
            for (var i = 2; i <= v.data.length-1; i++){
                var mean = (v.data[i][1] + v.data[i-1][1] + v.data[i-2][1])/3.0;
                moveMean[v.name]['data'].push([v.data[i][0],mean]);
            }
        }
    });
    return {series: Object.values(moveMean), ymax: pm25max}
}

function getYmax(pm25){
	if (pm25 < 9.1){
		return 9.1;
	}
	if (pm25 < 35.5){
		return 35.5;
	}
	if (pm25 < 55.5){
		return 55.5;
	}
	if (pm25 < 125.5){
		return 125.5;
	}
	if (pm25 < 225.5){
		return 225.5;
	}
	if (pm25 >= 225.5){
		return null;
	}
};

export default observer(function CountyPM25Multispline(props) {
    let seriesData = {};
    if (countyRecentPM25Store.data) {
        try {
            if (props.threeHour) {
                seriesData = countyRecentPM253hrData(countyRecentPM25Store.data);
            } else {
                seriesData = processCountyRecentPM25Data(countyRecentPM25Store.data);
            }
        } catch(e){}
    }

    let options = {
        chart: {
            type: 'spline'
        },

        title: {
            useHTML: true,
            text: `${props.county} County PM<sub>2.5</sub>`
        },

        time: {
            useUTC: true
        },

        yAxis: {
            title: {
                useHTML: true,
                text: 'PM<sub>2.5</sub> (&mu;g/m<sup>3</sup>)'
            },
            max: getYmax(seriesData['ymax']),
            endOnTick: false,
            alternateGridColor: null,
            plotBands: [{ // Good
                from: 0.0,
                to: 9.1,
                color: 'rgba(46, 204, 112, 0.3)',
            }, { // Moderate
                from: 9.1,
                to: 35.5,
                color: 'rgba(241, 196, 14, 0.1)',
            }, { // Unhealthy for Sensitive Groups
                from: 35.5,
                to: 55.5,
                color: 'rgba(230, 126, 34, 0.1)',
            }, { // Unhealthy
                from: 55.0,
                to: 125.5,
                color: 'rgba(231, 76, 60, .1)',
            }, { // Very Unhealthy
                from: 125.5,
                to: 225.5,
                color: 'rgba(209, 157, 215, 0.3)',
            }, { // Hazardous
                from: 225.5,
                to: 500,
                color: 'rgba(140, 58, 58, .1)',
            }]
        },

        xAxis: {
            type: 'datetime',
            xDateFormat: '%b %d %Y at %l%p',
        },

        legend: {
            align: 'center',
            verticalAlign: 'bottom',
            x: 0,
            y: 0
        },

        plotOptions: {
            series: {
                label: {
                    connectorAllowed: false,
                    enabled: false
                },
                marker: {
                    enabled: false
                },
                color: '#b3b3b3',
                gapSize: 1
            }
        },

        series: seriesData.series,

        tooltip: {
            valueDecimals: 1
        },

        responsive: {
            rules: [{
                condition: {
                    maxWidth: 500
                },
                chartOptions: {
                    legend: {
                        layout: 'horizontal',
                        align: 'center',
                        verticalAlign: 'bottom'
                    }
                }
            }]
        }
    }

    return (
        <div>
            <HighchartWithLoadingSpinner loaded={!!seriesData.series} options={options}
                dataLength={seriesData.series && seriesData.series.length} />
            <p><b>Caption:</b>
                Daily average (local midnight to midnight) values for each monitor in the county.
                The average across all monitors is also shown.
            </p>
            <MonitoringV4Links />
        </div>
    )
})
